import { YDatabase } from '@databases/collab/y-database';
import {
  CollabCardWidth,
  CollabCoverFieldType,
  CollabLayoutType,
  DatabaseViewJSON,
} from '@databases/model/views/view';
import { ViewId } from '@databases/types/database/view';
import { messages } from '@databases/views/messages';
import { getUniqueViewName } from '@databases/views/utils/viewName';
import type { IntlShape } from 'react-intl-next';
import { v4 as uuidv4 } from 'uuid';

const ALL_ENTRIES_VIEW_NAME = 'All entries';
const MODIFIED_ALL_ENTRIES_VIEW_NAME = 'All entries (edited)';

const findViewIdAndPosition = (
  target: ViewId.ALL_ENTRIES | ViewId.DEFAULT,
  ydb: YDatabase
): [string | undefined, number] => {
  const index = ydb.views.findIndex((view) => view === target);

  if (index !== -1) {
    return [ydb.views[index], index];
  }

  return [undefined, index];
};

const getOrCreateTargetView = (ydb: YDatabase, targetViewId: string, intl: IntlShape) => {
  if (!ydb.viewsById[targetViewId]) {
    ydb.setView(targetViewId, {
      name: intl.formatMessage(messages['view.default-view-name']),
      calculations: {},
      cardWidth: CollabCardWidth.MEDIUM,
      coverField: { type: CollabCoverFieldType.Default },
      dateFieldId: undefined,
      fields: undefined,
      filter: undefined,
      groupFieldId: undefined,
      layoutType: CollabLayoutType.TABLE,
      sort: undefined,
    });
  }

  return ydb.viewsById[targetViewId];
};

const convertView = (
  ydb: YDatabase,
  target: ViewId.ALL_ENTRIES | ViewId.DEFAULT,
  intl: IntlShape
) => {
  const [targetViewId, index] = findViewIdAndPosition(target, ydb);

  if (targetViewId) {
    const id = uuidv4();
    const targetView = getOrCreateTargetView(ydb, targetViewId, intl);
    const targetViewJson = targetView.toJSON();

    translateViewName(target, targetViewJson, intl);

    ydb.addView(id, targetViewJson);
    ydb.reorderView(id, index);
    ydb.deleteView(targetViewId);

    /**
     * We needed to reset the view id of a singular view regardless of its value
     * because of data consistency issues where defautlViewId may not match
     * any of the existing views' ids.
     */
    if (ydb.views.length === 1 || ydb.defaultViewId === targetViewId) {
      ydb.defaultViewId = id;
    }
  }
};

const translateViewName = (
  target: ViewId.ALL_ENTRIES | ViewId.DEFAULT,
  viewJson: DatabaseViewJSON,
  intl: IntlShape
) => {
  const { formatMessage } = intl;

  if (target === ViewId.ALL_ENTRIES) {
    viewJson.name = formatMessage(messages['view-settings.all-entries-view-name']);
  }

  if (target === ViewId.DEFAULT) {
    if (viewJson.name === ALL_ENTRIES_VIEW_NAME) {
      viewJson.name = formatMessage(messages['view-settings.all-entries-view-name']);
    }
    if (viewJson.name === MODIFIED_ALL_ENTRIES_VIEW_NAME) {
      viewJson.name = formatMessage(
        messages['toolbar-views.all-entries-view-name-modified']
      );
    }
  }
};

const resolveDefaultViewId = (ydb: YDatabase) => {
  if (!ydb.viewsById[ydb.defaultViewId]) {
    ydb.defaultViewId = ydb.views[0];
  }
};

const resolveEmptyOrDuplicateViewName = (ydb: YDatabase, intl: IntlShape) => {
  const ydbJson = ydb.toJSON();
  const viewNames: string[] = [];

  for (const [id, view] of Object.entries(ydbJson.viewsById)) {
    const newViewName = getUniqueViewName(view, viewNames, intl);

    viewNames.push(newViewName);

    if (view.name !== newViewName) {
      ydb.setView(id, { ...view, name: newViewName });
    }
  }
};

/**
 * This migration transforms existing views by doing the following:
 * - converts special views such as `all-entries` and `default` to normal views.
 * - sets defaultViewId if it's missing or invalid.
 * - creates unique names for empty or duplicate view names.
 */
export const transformViews = {
  version: 1725926197849, // timestamp of the migration when it's written
  apply(documentId: string, ydb: YDatabase, intl: IntlShape) {
    /**
     * Converts special views such as `all-entries` and `default` (used until Beta)
     * to a regular view
     */
    convertView(ydb, ViewId.ALL_ENTRIES, intl);
    convertView(ydb, ViewId.DEFAULT, intl);

    resolveDefaultViewId(ydb);
    resolveEmptyOrDuplicateViewName(ydb, intl);
  },
};
